import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Form, {
  Item,
  ButtonItem,
  GroupItem,
  Label,
} from "devextreme-react/form";
import useHandleSchedule from "../../../Hooks/useHandleData/useHandleSchedule";
import { isMobile } from "../../../Utils/media-query";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import "../styles.css";
const dayKeys = [
  "sundayInput",
  "mondayInput",
  "tuesdayInput",
  "wednesdayInput",
  "thursdayInput",
  "fridayInput",
  "saturdayInput",
];

const dayLabels = {
  sundayInput: "Sunday",
  mondayInput: "Monday",
  tuesdayInput: "Tuesday",
  wednesdayInput: "Wednesday",
  thursdayInput: "Thursday",
  fridayInput: "Friday",
  saturdayInput: "Saturday",
};
export default function Schedule({ selectedUser, isOtherUser, onClose }) {
  const title = selectedUser.name + "'s Schedule";
  const {
    scheduleData = [],
    handlePostAddSchedule,
    handlePutEditSchedule,
  } = useHandleSchedule(selectedUser.id);
  const [isSaveDisabled, setSaveDisable] = useState(true);
  const formRef = useRef(null);
  // single source of truth for all days

  const [days, setDays] = useState(
    Object.fromEntries(dayKeys.map((k) => [k, []]))
  );

  // keep the shape your API expects:
  const schedule = useMemo(() => days, [days]);

  useEffect(() => {
    const hasScheduleData = scheduleData?.length > 0;
    if (!hasScheduleData) return;

    // Map scheduleData[0..6] onto dayKeys in order
    const updated = {};
    dayKeys.forEach((k, i) => {
      updated[k] = scheduleData[i]?.employeeScheduleTime || [];
    });
    setDays(updated);
  }, [scheduleData]);

  //define function for removing a new input

  const removeHoursOptions = useCallback(
    (index, input, setInput, afterChange) => {
      return {
        stylingMode: "text",
        icon: "trash",

        onClick: () => {
          const newPhones = input
            .slice(0, index)
            .concat(input.slice(index + 1));
          setInput(newPhones);
          if (afterChange) afterChange(); // mark form dirty → enables Save
        },
      };
    },
    []
  );

  const addHoursOptions = useCallback((setState) => {
    return {
      text: "Add Hours",
      icon: "add",
      onClick: () => {
        const newTime = { startTime: null, endTime: null };
        setState((prev) => [...prev, newTime]);
      },
    };
  }, []);

  const onFieldDataChanged = useCallback(() => {
    if (isSaveDisabled) {
      setSaveDisable(false);
    }
  }, [setSaveDisable, isSaveDisabled]);

  const handleSave = useCallback(async () => {
    setSaveDisable(true);
    let formValidation = formRef.current.instance.validate();
    if (formValidation.isValid) {
      (await scheduleData.length) > 0
        ? handlePutEditSchedule(schedule)
        : handlePostAddSchedule(schedule);
    }
    setSaveDisable(false);
  }, [
    handlePostAddSchedule,
    handlePutEditSchedule,
    schedule,
    scheduleData.length,
  ]);

  const submitOptions = useMemo(() => {
    return {
      stylingMode: "contained",
      useSubmitBehavior: true,
      text: "Save",
      disabled: isSaveDisabled,
      onClick: () => handleSave(),
    };
  }, [isSaveDisabled, handleSave]);

  const cancelOptions = useMemo(() => {
    return {
      stylingMode: "outlined",
      text: "cancel",
      onClick: () => onClose(),
    };
  }, [onClose]);

  return (
    <Popup
      visible={true}
      width="50%"
      height="90%"
      fullScreen={isMobile}
      hideOnOutsideClick={true}
      onHiding={() => onClose()}
      title={title}
    >
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={cancelOptions}
      />
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={submitOptions}
      />
      <ScheduleForm
        schedule={schedule}
        addHoursOptions={addHoursOptions}
        removeHoursOptions={removeHoursOptions}
        setDays={setDays}
        formRef={formRef}
        onFieldDataChanged={onFieldDataChanged}
      />
    </Popup>
  );
}

function ScheduleForm({
  schedule,
  addHoursOptions,
  removeHoursOptions,
  setDays,
  formRef,
  onFieldDataChanged,
  disabled,
}) {
  const timeOptions = useMemo(
    () => ({
      type: "time",
      placeholder: "Please select a time",
      readOnly: disabled,
      dateSerializationFormat: "yyyy-MM-ddTHH:mm:ss",
    }),
    [disabled]
  );

  const setForDay = useCallback(
    (dayKey) => (newArray) =>
      setDays((prev) => ({ ...prev, [dayKey]: newArray })),
    [setDays]
  );

  return (
    <Form
      style={{ paddingLeft: "10px", paddingTop: "10px" }}
      onFieldDataChanged={onFieldDataChanged}
      ref={formRef}
      showValidationSummary={true}
      formData={schedule}
    >
      {dayKeys.map((dayKey) => (
        <GroupItem key={dayKey} caption={dayLabels[dayKey]}>
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={addHoursOptions((prev) =>
              // use functional update for this specific day
              setDays((all) => ({
                ...all,
                [dayKey]: [...all[dayKey], { startTime: null, endTime: null }],
              }))
            )}
          />
          {schedule[dayKey].map((_, index) => (
            <Item key={`${dayKey}${index}`} itemType="group" colCount={9}>
              <Item
                colSpan={4}
                isRequired={true}
                dataField={`${dayKey}[${index}].startTime`}
                editorType="dxDateBox"
                editorOptions={timeOptions}
              >
                <Label text="Start Time" />
              </Item>
              <Item
                colSpan={4}
                isRequired={true}
                dataField={`${dayKey}[${index}].endTime`}
                editorType="dxDateBox"
                editorOptions={timeOptions}
              >
                <Label text="End Time" />
              </Item>
              <ButtonItem
                colSpan={1}
                horizontalAlignment="left"
                verticalAlignment="bottom"
                buttonOptions={removeHoursOptions(
                  index,
                  schedule[dayKey],
                  setForDay(dayKey), // gives removeHoursOptions a per-day setter
                  onFieldDataChanged
                )}
              />
            </Item>
          ))}
        </GroupItem>
      ))}
    </Form>
  );
}
